import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// =========================================================
// HOVER MOSAIC TRANSITION
// =========================================================

export const initPopularGamesHoverMosaic = ({
  tileContainer,
  hoveredGame,
  activeHoverImageRef,
  hoverAnimationIdRef,
}) => {
  if (!tileContainer) return;

  const tiles = Array.from(
    tileContainer.querySelectorAll(".popular-games-hover-tile")
  );

  if (!tiles.length) return;

  const animationId = ++hoverAnimationIdRef.current;

  gsap.killTweensOf(tiles);

  const setTileImage = (image) => {
    tiles.forEach((tile) => {
      tile.style.backgroundImage = `url(${image})`;
    });
  };

  const revealTiles = (image) => {
    if (animationId !== hoverAnimationIdRef.current) return;

    setTileImage(image);

    gsap.set(tiles, {
      opacity: 0,
      scale: 0.72,
      x: () => gsap.utils.random(-45, 45),
      y: () => gsap.utils.random(-35, 35),
      rotation: () => gsap.utils.random(-5, 5),
    });

    gsap.to(tiles, {
      opacity: 0.95,
      scale: 1,
      x: 0,
      y: 0,
      rotation: 0,
      duration: 0.01,
      ease: "power3.out",
      stagger: {
        each: 0.002,
        from: "random",
      },
    });
  };

  const hideTiles = () => {
    gsap.to(tiles, {
      opacity: 0,
      scale: 0.88,
      x: () => gsap.utils.random(-20, 20),
      y: () => gsap.utils.random(-15, 15),
      rotation: () => gsap.utils.random(-2, 2),
      duration: 0.01,
      ease: "power2.in",
      stagger: {
        each: 0.012,
        from: "random",
      },
    });
  };

  if (!hoveredGame) {
    activeHoverImageRef.current = null;
    hideTiles();

    return () => gsap.killTweensOf(tiles);
  }

  const previousImage = activeHoverImageRef.current;
  const nextImage = hoveredGame.image;

  if (!previousImage) {
    activeHoverImageRef.current = nextImage;
    revealTiles(nextImage);
  } else if (previousImage !== nextImage) {
    activeHoverImageRef.current = nextImage;

    gsap.to(tiles, {
      opacity: 0,
      scale: 0.86,
      x: () => gsap.utils.random(-25, 25),
      y: () => gsap.utils.random(-20, 20),
      rotation: () => gsap.utils.random(-3, 3),
      duration: 0.22,
      ease: "power2.in",
      stagger: {
        each: 0.008,
        from: "random",
      },
      onComplete: () => {
        if (animationId !== hoverAnimationIdRef.current) return;
        revealTiles(nextImage);
      },
    });
  }

  return () => gsap.killTweensOf(tiles);
};

// =========================================================
// POPULAR + LIVE SCROLL ANIMATIONS
// =========================================================

export const initPopularGamesAnimations = ({
  sectionRef,
  newlyAddedSectionRef,
  liveSectionRef,
  setHoveredGame,
}) => {
  const section = sectionRef.current;
  const newlyAddedSection = newlyAddedSectionRef?.current;
  const liveSection = liveSectionRef?.current;

  if (!section || !newlyAddedSection || !liveSection) return;

  const context = gsap.context(() => {
    const cards = section.querySelectorAll(".popular-games-card");

    if (!cards.length) return;


    const popularContent = section.children;
    const newlyAddedContent = newlyAddedSection.children;

    // Keep Popular Games fully visible before the transition.
    gsap.set(popularContent, {
      opacity: 1,
    });
    
    gsap.set(newlyAddedContent, {
      opacity: 1,
    });

    // ---------------------------------------------------------
    // PIN POPULAR GAMES UNTIL NEWLY ADDED COVERS IT
    // ---------------------------------------------------------
    ScrollTrigger.create({
      trigger: section,
      start: "top top+=60",
      endTrigger: newlyAddedSection,
      end: "top top+=60",
      pin: section,
      pinSpacing: false,
      invalidateOnRefresh: true,
    });

    // ---------------------------------------------------------
    // SLOW FADE POPULAR GAMES WHILE NEWLY ADDED MOVES OVER IT
    // ---------------------------------------------------------
    gsap.to(popularContent, {
      opacity: 0,
      ease: "none",
      scrollTrigger: {
        trigger: newlyAddedSection,
        start: "top 95%",
        end: "top top+=60",
        scrub: 1.2,
        invalidateOnRefresh: true,
      },
    });

    // ---------------------------------------------------------
    // PIN NEWLY ADDED UNTIL LIVE GAMES COVERS IT
    // ---------------------------------------------------------
    ScrollTrigger.create({
      trigger: newlyAddedSection,
      start: "top top+=60",
      endTrigger: liveSection,
      end: "top top+=60",
      pin: newlyAddedSection,
      pinSpacing: false,
      invalidateOnRefresh: true,
    });

    // ---------------------------------------------------------
    // SLOW FADE NEWLY ADDED WHILE LIVE GAMES MOVES OVER IT
    // ---------------------------------------------------------
    gsap.to(newlyAddedContent, {
      opacity: 0,
      ease: "none",
      scrollTrigger: {
        trigger: liveSection,
        start: "top 95%",
        end: "top top+=60",
        scrub: 1.2,
        invalidateOnRefresh: true,
      },
    });

    // ---------------------------------------------------------
    // REFRESH AFTER LAYOUT IS READY
    // ---------------------------------------------------------

    requestAnimationFrame(() => {
      ScrollTrigger.refresh();
    });
  }, section);

  return () => context.revert();
};